import { API_BASE_URL, getToken, get, post, removeToken } from "@/lib/api-client-v2";

export type NomenclatureObjectHit = {
  id: number;
  name: string;
  organization_id?: number | null;
};

export type NomenclatureSheetHit = {
  nomenclature: string;
  scale: number;
  geom: GeoJSON.Polygon;
  objects: NomenclatureObjectHit[];
};

export type NomenclatureIntersectResponse = {
  sheets: NomenclatureSheetHit[];
  buffer_m: number;
  include_zones: boolean;
  scales: number[];
  sheet_count: number;
  object_count: number;
};

export type NomenclatureIntersectBody = {
  scales: number[];
  buffer_m?: number;
  include_zones?: boolean;
  object_ids?: number[] | null;
  limit_sheets?: number;
};

export const nomenclatureSheetsApi = {
  meta: () =>
    get<{ scales: { code: number; label: string }[] }>(
      "/oi/nomenclature-sheets/meta",
    ),

  geojson: (params: {
    scale: number;
    west: number;
    south: number;
    east: number;
    north: number;
    limit?: number;
  }) =>
    get<GeoJSON.FeatureCollection>("/oi/nomenclature-sheets/geojson", {
      scale: params.scale,
      west: params.west,
      south: params.south,
      east: params.east,
      north: params.north,
      limit: params.limit ?? 800,
    }),

  intersect: (body: NomenclatureIntersectBody) =>
    post<NomenclatureIntersectResponse>(
      "/oi/nomenclature-sheets/intersect",
      {
        scales: body.scales,
        buffer_m: body.buffer_m ?? 0,
        include_zones: body.include_zones ?? true,
        object_ids: body.object_ids ?? null,
        limit_sheets: body.limit_sheets ?? 2000,
      },
    ),

  exportXlsx: async (body: NomenclatureIntersectBody): Promise<Blob> => {
    const url = `${API_BASE_URL}/oi/nomenclature-sheets/intersect/export`;
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(url, {
      method: "POST",
      headers,
      credentials: "same-origin",
      body: JSON.stringify({
        scales: body.scales,
        buffer_m: body.buffer_m ?? 0,
        include_zones: body.include_zones ?? true,
        object_ids: body.object_ids ?? null,
        limit_sheets: body.limit_sheets ?? 2000,
      }),
    });
    if (response.status === 401) {
      removeToken();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      throw new Error("Unauthorized");
    }
    if (!response.ok) {
      let msg = `HTTP ${response.status}`;
      try {
        const err = await response.json();
        if (typeof err.detail === "string") msg = err.detail;
      } catch {
        /* ignore */
      }
      throw new Error(msg);
    }
    return response.blob();
  },
};
